import { useEffect, useRef, useState, useCallback } from "react";
import { WS_URL } from "./api";

// Shared realtime hook. Subscribes to backend WebSocket and delivers events.
export function useRealtime(onEvent) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  const connect = useCallback(() => {
    try {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        setTimeout(connect, 2000);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          cbRef.current && cbRef.current(data);
        } catch (_) {}
      };
    } catch (_) {
      setTimeout(connect, 2000);
    }
  }, []);

  useEffect(() => {
    connect();
    const ping = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === 1) wsRef.current.send("ping");
    }, 20000);
    return () => {
      clearInterval(ping);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { connected };
}

// Simple synthesized sound effects (no external assets required).
export const sfx = {
  ctx: null,
  ensure() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) this.ctx = new AC();
    }
    return this.ctx;
  },
  beep(freq = 440, dur = 0.06, vol = 0.4, type = "square") {
    const ctx = this.ensure();
    if (!ctx) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.value = vol;
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    o.stop(ctx.currentTime + dur);
  },
  tick(vol) { this.beep(880, 0.03, vol * 0.25, "square"); },
  start(vol) { this.beep(220, 0.2, vol * 0.5, "sawtooth"); },
  result(vol) {
    this.beep(523, 0.12, vol * 0.5);
    setTimeout(() => this.beep(784, 0.2, vol * 0.5), 120);
  },
  sub(vol) {
    this.beep(659, 0.12, vol * 0.5);
    setTimeout(() => this.beep(880, 0.25, vol * 0.5), 130);
  },
};
